import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import "./Editor.scss";

const EMPTY_DOC = { type: "doc", content: [{ type: "paragraph" }] };

function parseInitial(value) {
    if (!value) return EMPTY_DOC;
    if (typeof value === "object") return value;
    try {
        return JSON.parse(value);
    } catch {
        return EMPTY_DOC;
    }
}

function ToolbarButton({ active, disabled, onClick, label, children }) {
    return (
        <button
            type="button"
            className={"editor-tb-btn" + (active ? " is-active" : "")}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            title={label}
        >
            {children}
        </button>
    );
}

export default function Editor({ value, onChange, onStats, placeholder = "ابدأ الكتابة…" }) {

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
            }),
            Underline,
            Placeholder.configure({ placeholder }),
            CharacterCount.configure(),
        ],
        content: parseInitial(value),
        onUpdate: ({ editor }) => {
            onChange?.(editor.getJSON());
            onStats?.({ words: editor.storage.characterCount.words(), characters: editor.storage.characterCount.characters() });
        },
        onCreate: ({ editor }) => {
            onStats?.({ words: editor.storage.characterCount.words(), characters: editor.storage.characterCount.characters() });
        },
    }, []);

    React.useEffect(() => {
        if (!editor) return;
        const current = JSON.stringify(editor.getJSON());
        const next = JSON.stringify(parseInitial(value));
        if (current !== next) {
            editor.commands.setContent(parseInitial(value), false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    if (!editor) return null;

    return (
        <div className="editor">
            <div className="editor-toolbar" role="toolbar" aria-label="تنسيق النص">
                <ToolbarButton
                    label="عريض"
                    active={editor.isActive('bold')}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                >
                    <b>B</b>
                </ToolbarButton>
                <ToolbarButton
                    label="مائل"
                    active={editor.isActive('italic')}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                    <i>I</i>
                </ToolbarButton>
                <ToolbarButton
                    label="تسطير"
                    active={editor.isActive('underline')}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                >
                    <u>U</u>
                </ToolbarButton>
                <ToolbarButton
                    label="يتوسطه خط"
                    active={editor.isActive('strike')}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                >
                    <s>S</s>
                </ToolbarButton>

                <span className="editor-tb-sep" />

                <ToolbarButton
                    label="عنوان كبير"
                    active={editor.isActive('heading', { level: 1 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                >H1</ToolbarButton>
                <ToolbarButton
                    label="عنوان متوسط"
                    active={editor.isActive('heading', { level: 2 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                >H2</ToolbarButton>
                <ToolbarButton
                    label="عنوان صغير"
                    active={editor.isActive('heading', { level: 3 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                >H3</ToolbarButton>

                <span className="editor-tb-sep" />

                <ToolbarButton
                    label="قائمة نقطية"
                    active={editor.isActive('bulletList')}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                >•</ToolbarButton>
                <ToolbarButton
                    label="قائمة مرقمة"
                    active={editor.isActive('orderedList')}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                >1.</ToolbarButton>
                <ToolbarButton
                    label="اقتباس"
                    active={editor.isActive('blockquote')}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                >❝</ToolbarButton>

                <span className="editor-tb-sep" />

                <ToolbarButton
                    label="تراجع"
                    disabled={!editor.can().undo()}
                    onClick={() => editor.chain().focus().undo().run()}
                >↶</ToolbarButton>
                <ToolbarButton
                    label="إعادة"
                    disabled={!editor.can().redo()}
                    onClick={() => editor.chain().focus().redo().run()}
                >↷</ToolbarButton>
            </div>
            <EditorContent editor={editor} className="editor-content" />
            <div className="editor-footer">
                <span className="editor-wordcount">
                    {editor.storage.characterCount.words().toLocaleString('ar-EG')} كلمة
                    {' · '}
                    {editor.storage.characterCount.characters().toLocaleString('ar-EG')} حرفًا
                </span>
            </div>
        </div>
    );
}
